import React, {useContext, useState} from "react";
import {motion} from "framer-motion";
import "./GitHub.scss";
import {engineeringWork} from "../../portfolio";
import StyleContext from "../../contexts/StyleContext";
import {fadeUp, staggerCards, cardItem, vp} from "../../utils/animations";

const GitHubIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
  </svg>
);

const ArrowIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2.5 9.5L9.5 2.5M9.5 2.5H5M9.5 2.5V7" />
  </svg>
);

const LiveIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="6.5" />
    <path d="M8 4.5v3.5l2.2 1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ── Standard card — used for all non-featured systems ── */
function StandardCard({system}) {
  const [expanded, setExpanded] = useState(false);
  const linkLabel = system.linkLabel || "GitHub";

  return (
    <article className="sys-card">
      {/* Header */}
      <div className="sys-card__header">
        <div className="sys-card__title-row">
          <span className="sys-card__icon">
            {linkLabel === "GitHub" ? <GitHubIcon /> : <LiveIcon />}
          </span>
          <h3 className="sys-card__name">{system.name}</h3>
        </div>
        {system.link && (
          <a
            href={system.link}
            target="_blank"
            rel="noopener noreferrer"
            className="sys-card__link"
            aria-label={`${linkLabel === "GitHub" ? "View" : "Visit"} ${
              system.name
            }${linkLabel === "GitHub" ? " on GitHub" : ""}`}
          >
            <ArrowIcon />
          </a>
        )}
      </div>

      {/* Metrics */}
      {system.metrics && system.metrics.length > 0 && (
        <div className="sys-card__metrics">
          {system.metrics.map((m, k) => (
            <div key={k} className="sys-card__metric">
              <span className="sys-card__metric-val">{m.value}</span>
              <span className="sys-card__metric-lbl">{m.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Business value — PM-facing row */}
      {system.pmValue && (
        <div className="sys-card__field sys-card__field--pm">
          <span className="sys-card__field-label sys-card__field-label--pm">
            For&nbsp;teams
          </span>
          <p className="sys-card__field-body sys-card__field-body--pm">
            {system.pmValue}
          </p>
        </div>
      )}

      {/* Problem */}
      <div className="sys-card__field">
        <span className="sys-card__field-label">Problem</span>
        <p className="sys-card__field-body">{system.problem}</p>
      </div>

      {/* Approach — line-clamped by default, toggle to expand */}
      <div
        className={`sys-card__field sys-card__field--approach${
          expanded ? " is-expanded" : ""
        }`}
      >
        <span className="sys-card__field-label">Approach</span>
        <p className="sys-card__field-body">{system.approach}</p>
      </div>

      {/* Impact */}
      <div className="sys-card__field sys-card__field--impact">
        <span className="sys-card__field-label">Impact</span>
        <p className="sys-card__field-body sys-card__field-body--impact">
          {system.impact}
        </p>
      </div>

      {/* Footer */}
      <div className="sys-card__footer">
        <div className="sys-card__tech">
          {system.tech.map((t, i) => (
            <span key={i} className="tech-tag">
              {t}
            </span>
          ))}
        </div>
        <button
          className="sys-card__expand"
          onClick={() => setExpanded(v => !v)}
          aria-expanded={expanded}
          aria-label={expanded ? "Show less" : "Show approach in full"}
        >
          {expanded ? "Less" : "More"}
        </button>
      </div>
    </article>
  );
}

/* ── Featured card — full-width case study layout ── */
function FeaturedCard({system}) {
  const linkLabel = system.linkLabel || "GitHub";

  return (
    <article className="sys-card sys-card--featured">
      {/* Header */}
      <div className="sys-card__header">
        <div className="sys-card__title-row">
          <span className="sys-card__icon">
            {linkLabel === "GitHub" ? <GitHubIcon /> : <LiveIcon />}
          </span>
          <h3 className="sys-card__name">{system.name}</h3>
          <span className="sys-card__badge">Case Study</span>
        </div>
        {system.link && (
          <a
            href={system.link}
            target="_blank"
            rel="noopener noreferrer"
            className="sys-card__link"
            aria-label={`${linkLabel === "GitHub" ? "View" : "Visit"} ${
              system.name
            }${linkLabel === "GitHub" ? " on GitHub" : ""}`}
          >
            <ArrowIcon />
          </a>
        )}
      </div>

      {/* Metrics strip */}
      {system.metrics && system.metrics.length > 0 && (
        <div className="sys-card__metrics">
          {system.metrics.map((m, k) => (
            <div key={k} className="sys-card__metric">
              <span className="sys-card__metric-val">{m.value}</span>
              <span className="sys-card__metric-lbl">{m.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Business value — spans full width, prominent */}
      {system.pmValue && (
        <div className="sys-card__field sys-card__field--pm">
          <span className="sys-card__field-label sys-card__field-label--pm">
            For&nbsp;teams
          </span>
          <p className="sys-card__field-body sys-card__field-body--pm">
            {system.pmValue}
          </p>
        </div>
      )}

      {/* Two-column body: Problem left · Approach + Impact right */}
      <div className="sys-card__featured-body">
        <div className="sys-card__featured-col">
          <div className="sys-card__field">
            <span className="sys-card__field-label">Problem</span>
            <p className="sys-card__field-body">{system.problem}</p>
          </div>
        </div>
        <div className="sys-card__featured-col">
          <div className="sys-card__field">
            <span className="sys-card__field-label">Approach</span>
            <p className="sys-card__field-body">{system.approach}</p>
          </div>
          <div className="sys-card__field sys-card__field--impact">
            <span className="sys-card__field-label">Impact</span>
            <p className="sys-card__field-body sys-card__field-body--impact">
              {system.impact}
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="sys-card__footer">
        <div className="sys-card__tech">
          {system.tech.map((t, i) => (
            <span key={i} className="tech-tag">
              {t}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}

export default function GitHub() {
  const {isDark} = useContext(StyleContext);
  if (!engineeringWork || !engineeringWork.display) return null;

  return (
    <motion.section
      className={`ew-section ${isDark ? "dark" : ""}`}
      id="engineering"
      initial="hidden"
      whileInView="show"
      viewport={vp}
      variants={fadeUp}
    >
      <div className="ew__inner">
        {/* Section header */}
        <motion.div className="ew__header" variants={fadeUp}>
          <span className="section-label">Engineering Work</span>
          <div className="ew__title-row">
            <h2 className="ew__title">{engineeringWork.title}</h2>
            <a
              href={engineeringWork.profileLink}
              target="_blank"
              rel="noopener noreferrer"
              className="ew__profile-link"
            >
              @{engineeringWork.username} ↗
            </a>
          </div>
          <p className="ew__tagline">{engineeringWork.tagline}</p>
        </motion.div>

        {/* Systems grid */}
        <motion.div
          className="ew__grid"
          variants={staggerCards}
          initial="hidden"
          whileInView="show"
          viewport={{once: true, amount: 0.05}}
        >
          {engineeringWork.systems.map((system, i) => (
            <motion.div
              key={i}
              variants={cardItem}
              className={system.featured ? "ew__grid-featured" : ""}
            >
              {system.featured ? (
                <FeaturedCard system={system} />
              ) : (
                <StandardCard system={system} />
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
}
